import { debtorCompanyPrimaryName } from './debtorIdentity.js';
import { matterDemandId } from './demandId.js';
import {
  loadPersistedSort,
  savePersistedSort,
  sortRowsByColumn,
} from './listColumnSort.js';
import { formatMatterMoney } from './matterCurrency.js';
import {
  isPaidStatus,
  listMatterPaymentEntries,
  matterAmountOwed,
  matterAmountPaid,
} from './paidBeforeAssignment.js';

export const DEFAULT_PAYMENT_FILTER = 'all';

export const PAYMENT_FILTER_OPTIONS = Object.freeze([
  { id: 'all', label: 'All' },
  { id: 'synced', label: 'Synced to accounting' },
  { id: 'unsynced', label: 'Not synced' },
  { id: 'full', label: 'Full payment' },
  { id: 'partial', label: 'Partial payment' },
]);

const SORT_STORAGE_KEY = 'demandjet.paymentsListSort';

export const PAYMENT_LIST_COLUMNS = Object.freeze([
  { id: 'paidAt', label: 'Date', sortType: 'date' },
  { id: 'debtorName', label: 'Debtor', sortType: 'string' },
  { id: 'demandId', label: 'Demand ID', sortType: 'string' },
  { id: 'amount', label: 'Amount', sortType: 'number' },
  { id: 'kind', label: 'Type', sortType: 'string' },
  { id: 'reference', label: 'Reference', sortType: 'string' },
  { id: 'accounting', label: 'Accounting', sortType: 'string' },
]);

export const DEFAULT_PAYMENT_SORT = Object.freeze({
  columnId: 'paidAt',
  direction: 'desc',
});

export function paymentSortFieldLabel(columnId) {
  return PAYMENT_LIST_COLUMNS.find((c) => c.id === columnId)?.label || columnId;
}

export function isValidPaymentSortColumn(columnId) {
  return PAYMENT_LIST_COLUMNS.some((c) => c.id === columnId);
}

export function loadPaymentListSort() {
  return loadPersistedSort(SORT_STORAGE_KEY, DEFAULT_PAYMENT_SORT, isValidPaymentSortColumn);
}

export function savePaymentListSort(sort) {
  return savePersistedSort(
    SORT_STORAGE_KEY,
    sort,
    DEFAULT_PAYMENT_SORT,
    isValidPaymentSortColumn
  );
}

function paymentSortRawValue(row, columnId) {
  switch (columnId) {
    case 'paidAt':
      return Number(row?.paidAtMs) || 0;
    case 'debtorName':
      return String(row?.debtorName || '').toLowerCase();
    case 'demandId':
      return String(row?.demandId || '').toLowerCase();
    case 'amount':
      return Number(row?.amount) || 0;
    case 'kind':
      return String(row?.kindLabel || row?.kind || '').toLowerCase();
    case 'reference':
      return String(row?.reference || '').toLowerCase();
    case 'accounting':
      return String(row?.syncLabel || '').toLowerCase();
    default:
      return '';
  }
}

export function sortPaymentRecords(rows = [], sort = DEFAULT_PAYMENT_SORT) {
  return sortRowsByColumn(
    rows,
    {
      columnId: sort?.columnId || DEFAULT_PAYMENT_SORT.columnId,
      direction: sort?.direction,
    },
    paymentSortRawValue,
    (row) => String(row?.id || '')
  );
}

function paidAtMs(value) {
  const ms = Date.parse(String(value || ''));
  return Number.isFinite(ms) ? ms : 0;
}

function formatWhen(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return String(value);
  }
}

function debtorLabel(matter) {
  return (
    debtorCompanyPrimaryName(matter) ||
    String(matter?.Debtor_Name || matter?.Debtor_Company || '').trim() ||
    '—'
  );
}

function synthesizePaidEntry(matter) {
  const paid = matterAmountPaid(matter);
  const owed = matterAmountOwed(matter);
  const amount = paid > 0 ? paid : owed;
  if (!(amount > 0) && !isPaidStatus(matter?.Status)) return null;
  return {
    id: `pay_synth_${matter.ROWID}`,
    amount: amount > 0 ? amount : owed,
    currency: String(matter?.Currency || matter?.currency || 'USD').trim().toUpperCase() || 'USD',
    paidAt: matter?.Paid_At || matter?.Updated_At || matter?.Created_At || null,
    source: matter?.Paid_Source || 'recorded',
    note: matter?.Resolution_Note || null,
    reference: null,
    attachments: [],
    accountingPaymentId: null,
    accountingProvider: null,
    synthesized: true,
  };
}

function paymentKind(entry, matter) {
  const owed = matterAmountOwed(matter);
  const paidTotal = matterAmountPaid(matter);
  if (isPaidStatus(matter?.Status) || (owed > 0 && paidTotal >= owed - 0.001)) {
    return 'full';
  }
  return 'partial';
}

function toPaymentRecord(matter, entry) {
  const currency = entry.currency || matter?.Currency || matter?.currency || 'USD';
  const kind = paymentKind(entry, matter);
  const synced = Boolean(entry.accountingPaymentId);
  return {
    id: `${matter.ROWID}:${entry.id}`,
    paymentId: entry.id,
    matterId: String(matter.ROWID),
    demandId: matterDemandId(matter) || '',
    debtorName: debtorLabel(matter),
    amount: Number(entry.amount) || 0,
    currency,
    amountLabel: formatMatterMoney(entry.amount, currency),
    paidAt: entry.paidAt || null,
    paidAtLabel: formatWhen(entry.paidAt),
    paidAtMs: paidAtMs(entry.paidAt),
    reference: entry.reference || '',
    source: entry.source || '',
    note: entry.note || '',
    kind,
    kindLabel: kind === 'full' ? 'Full' : 'Partial',
    synced,
    syncLabel: synced
      ? entry.accountingProvider
        ? `Synced (${entry.accountingProvider})`
        : 'Synced'
      : 'Not synced',
    accountingPaymentId: entry.accountingPaymentId || '',
    accountingProvider: entry.accountingProvider || '',
    attachments: Array.isArray(entry.attachments) ? entry.attachments : [],
    matterStatus: matter?.Status || '',
    synthesized: Boolean(entry.synthesized),
  };
}

/** Flatten payment ledger rows across all demand records (newest first). */
export function listPaymentRecords(matters = []) {
  const rows = [];
  for (const matter of matters || []) {
    if (!matter?.ROWID) continue;
    const entries = listMatterPaymentEntries(matter);
    if (entries.length) {
      for (const entry of entries) {
        rows.push(toPaymentRecord(matter, entry));
      }
      continue;
    }
    const synthesized = synthesizePaidEntry(matter);
    if (synthesized) rows.push(toPaymentRecord(matter, synthesized));
  }
  return rows.sort((a, b) => {
    if (a.paidAtMs !== b.paidAtMs) return b.paidAtMs - a.paidAtMs;
    return String(b.id).localeCompare(String(a.id));
  });
}

export function isValidPaymentFilter(filterId) {
  return PAYMENT_FILTER_OPTIONS.some((option) => option.id === filterId);
}

export function paymentMatchesFilter(row, filterId = DEFAULT_PAYMENT_FILTER) {
  const filter = isValidPaymentFilter(filterId) ? filterId : DEFAULT_PAYMENT_FILTER;
  if (filter === 'all') return true;
  if (filter === 'synced') return Boolean(row?.synced);
  if (filter === 'unsynced') return !row?.synced;
  if (filter === 'full') return row?.kind === 'full';
  if (filter === 'partial') return row?.kind === 'partial';
  return true;
}

export function filterPaymentRecords(rows = [], filterId = DEFAULT_PAYMENT_FILTER) {
  const filter = isValidPaymentFilter(filterId) ? filterId : DEFAULT_PAYMENT_FILTER;
  if (filter === 'all') return rows || [];
  return (rows || []).filter((row) => paymentMatchesFilter(row, filter));
}

export function searchPaymentRecords(rows = [], query = '') {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return rows || [];
  return (rows || []).filter((row) => {
    const haystack = [
      row.debtorName,
      row.demandId,
      row.reference,
      row.amountLabel,
      row.syncLabel,
      row.kindLabel,
      row.matterId,
      row.source,
      row.note,
    ]
      .map((value) => String(value || '').toLowerCase())
      .join(' ');
    return haystack.includes(q);
  });
}

export function refinePaymentRecords(
  rows = [],
  { query = '', filterId = DEFAULT_PAYMENT_FILTER } = {}
) {
  return filterPaymentRecords(searchPaymentRecords(rows, query), filterId);
}

export function paymentDemandHref(row) {
  const matterId = String(row?.matterId || '').trim();
  if (!matterId) return '/client/demands';
  return `/client/demands/${encodeURIComponent(matterId)}?tab=payments`;
}
